import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { format, subDays } from "date-fns";
import clsx from "clsx";
import { alpha, makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import ButtonBase from "@material-ui/core/ButtonBase";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { ADVISOR_COLORS } from "../../components/AdvisorsPanel";

const REFRESH_MS = 60 * 1000;
const PERIODS = [
  { id: "today", days: 1 },
  { id: "week", days: 7 },
  { id: "month", days: 30 },
];

const warningTones = (theme) =>
  theme.palette.type === "dark"
    ? { bg: alpha("#FFAF04", 0.16), border: alpha("#FFAF04", 0.3), fg: "#FFC25C" }
    : { bg: "#FFF4E5", border: "#FBE1BC", fg: "#8A4B00" };

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    maxWidth: 1280,
    margin: "0 auto",
    padding: theme.spacing(3.5, 3, 5),
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2, 2, 4),
    },
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
  },
  title: {
    margin: 0,
    fontSize: "1.625rem",
    fontWeight: 700,
  },
  subtitle: {
    margin: 0,
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  periods: {
    display: "flex",
    gap: theme.spacing(0.75),
  },
  periodButton: {
    height: 36,
    padding: theme.spacing(0, 1.75),
    borderRadius: theme.borderRadius.pill,
    border: theme.border.divider,
    backgroundColor: theme.palette.cards.background,
    color: theme.palette.text.primary,
    fontSize: "0.8125rem",
    fontWeight: 600,
  },
  periodButtonSelected: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "minmax(0, 1fr)",
    },
  },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.25),
    padding: theme.spacing(2.5),
    borderRadius: theme.borderRadius.medium,
    backgroundColor: theme.palette.cards.background,
    border: theme.border.divider,
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
  },
  identity: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  avatar: {
    flex: "none",
    width: 44,
    height: 44,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: 600,
  },
  advisorName: {
    margin: 0,
    fontSize: "1.0625rem",
    fontWeight: 700,
  },
  status: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
  },
  viewChats: {
    flex: "none",
    padding: theme.spacing(1, 1.25),
    borderRadius: theme.borderRadius.small,
    backgroundColor: theme.palette.tintedBackground,
    color: theme.palette.primary.main,
    fontSize: "0.8125rem",
    fontWeight: 600,
  },
  metrics: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(1.25),
  },
  metric: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: theme.spacing(1.5),
    borderRadius: 12,
    border: theme.border.divider,
    backgroundColor: theme.palette.background.default,
  },
  metricWaiting: {
    backgroundColor: warningTones(theme).bg,
    borderColor: warningTones(theme).border,
    "& $metricValue, & $metricLabel": { color: warningTones(theme).fg },
  },
  metricLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  metricValue: {
    fontSize: "1.625rem",
    fontWeight: 700,
    lineHeight: 1.2,
  },
  sectionTitle: {
    margin: 0,
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  labelRow: {
    display: "grid",
    gridTemplateColumns: "96px minmax(0, 1fr) 28px",
    alignItems: "center",
    gap: theme.spacing(1.25),
  },
  labelTag: {
    justifySelf: "start",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.borderRadius.pill,
    color: "#1C1A22",
    fontSize: "0.6875rem",
    fontWeight: 700,
  },
  bar: {
    height: 8,
    borderRadius: theme.borderRadius.pill,
    backgroundColor: theme.palette.action.selected,
    overflow: "hidden",
  },
  barFill: {
    display: "block",
    height: "100%",
    borderRadius: theme.borderRadius.pill,
  },
  labelCount: {
    textAlign: "right",
    fontSize: "0.8125rem",
    fontWeight: 600,
  },
  empty: {
    margin: 0,
    color: theme.palette.text.secondary,
    fontSize: "0.8125rem",
  },
  tableCard: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2.5),
    borderRadius: theme.borderRadius.medium,
    backgroundColor: theme.palette.cards.background,
    border: theme.border.divider,
  },
  tableTitle: {
    margin: 0,
    fontSize: "1.0625rem",
    fontWeight: 700,
  },
  tableScroll: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    minWidth: 560,
    borderCollapse: "collapse",
    fontSize: "0.875rem",
    "& th": {
      padding: theme.spacing(1, 1.25),
      textAlign: "left",
      fontSize: "0.75rem",
      fontWeight: 600,
      color: theme.palette.text.secondary,
    },
    "& td": {
      padding: theme.spacing(1.5, 1.25),
      borderTop: theme.border.divider,
    },
  },
  rowButton: {
    font: "inherit",
    fontWeight: 600,
    color: theme.palette.primary.main,
    textAlign: "left",
  },
  waitingTime: {
    fontWeight: 600,
    color: warningTones(theme).fg,
  },
  tags: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  footnote: {
    margin: 0,
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
}));

const initialsOf = (name) =>
  (name || "?")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return "—";
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
};

const STATUS_COLORS = {
  CONNECTED: "#2E9E52",
  qrcode: "#D98200",
  OPENING: "#D98200",
  PAIRING: "#D98200",
};

const Summary = () => {
  const classes = useStyles();
  const history = useHistory();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const [period, setPeriod] = useState("today");
  const [summary, setSummary] = useState([]);
  const [waitingTickets, setWaitingTickets] = useState([]);
  const [responseTimes, setResponseTimes] = useState([]);

  useEffect(() => {
    const fetchLive = async () => {
      try {
        const [{ data: summaryData }, { data: waitingData }] = await Promise.all([
          api.get("/tickets/summary"),
          api.get("/tickets/waiting"),
        ]);
        setSummary(summaryData);
        setWaitingTickets(waitingData);
      } catch (err) {
        toastError(err);
      }
    };

    void fetchLive();
    const interval = setInterval(fetchLive, REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchResponseTimes = async () => {
      // The response-times report is admin-only
      if (user.profile !== "admin") return;
      const { days } = PERIODS.find((p) => p.id === period);
      const today = new Date();
      try {
        const { data } = await api.get("/reports/response-times", {
          params: {
            from: format(subDays(today, days - 1), "yyyy-MM-dd"),
            to: format(today, "yyyy-MM-dd"),
            groupBy: "whatsapp",
          },
        });
        setResponseTimes(data.rows || []);
      } catch (err) {
        toastError(err);
      }
    };

    void fetchResponseTimes();
  }, [period, user.profile]);

  const advisors = whatsApps.map((whatsApp, index) => {
    const counts = summary.find((s) => s.whatsappId === whatsApp.id) || {
      active: 0,
      waiting: 0,
      labels: [],
    };
    const times = responseTimes.find((r) => r.userId === whatsApp.id);
    const maxLabel = Math.max(1, ...counts.labels.map((l) => l.count));

    return {
      ...whatsApp,
      color: ADVISOR_COLORS[index % ADVISOR_COLORS.length],
      initials: initialsOf(whatsApp.name),
      active: counts.active,
      waiting: counts.waiting,
      responseAvg: formatDuration(times?.responseAvg),
      labels: counts.labels.map((l) => ({
        ...l,
        pct: `${Math.round((l.count / maxLabel) * 100)}%`,
      })),
    };
  });

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <h1 className={classes.title}>{i18n.t("summary.title")}</h1>
          <p className={classes.subtitle}>{i18n.t("summary.subtitle")}</p>
        </div>
        <div
          className={classes.periods}
          role="group"
          aria-label={i18n.t("summary.periodLabel")}
        >
          {PERIODS.map((p) => (
            <ButtonBase
              key={p.id}
              component="button"
              focusRipple
              aria-pressed={period === p.id}
              onClick={() => setPeriod(p.id)}
              className={clsx(
                classes.periodButton,
                period === p.id && classes.periodButtonSelected
              )}
            >
              {i18n.t(`summary.periods.${p.id}`)}
            </ButtonBase>
          ))}
        </div>
      </div>

      <div className={classes.cards}>
        {advisors.map((advisor) => (
          <article key={advisor.id} className={classes.card}>
            <div className={classes.cardHeader}>
              <div className={classes.identity}>
                <span
                  className={classes.avatar}
                  style={{ backgroundColor: advisor.color }}
                >
                  {advisor.initials}
                </span>
                <div>
                  <h2 className={classes.advisorName}>{advisor.name}</h2>
                  <span className={classes.status}>
                    <span
                      className={classes.statusDot}
                      style={{
                        backgroundColor: STATUS_COLORS[advisor.status] || "#9A96A6",
                      }}
                    />
                    {i18n.t(`advisors.status.${advisor.status}`, {
                      defaultValue: i18n.t("advisors.status.DISCONNECTED"),
                    })}
                  </span>
                </div>
              </div>
              <ButtonBase
                component="button"
                focusRipple
                className={classes.viewChats}
                onClick={() =>
                  history.push({
                    pathname: "/tickets",
                    state: { whatsappId: advisor.id },
                  })
                }
              >
                {i18n.t("summary.viewChats")}
              </ButtonBase>
            </div>

            <div className={classes.metrics}>
              <div className={classes.metric}>
                <span className={classes.metricLabel}>{i18n.t("summary.active")}</span>
                <span className={classes.metricValue}>{advisor.active}</span>
              </div>
              <div className={clsx(classes.metric, classes.metricWaiting)}>
                <span className={classes.metricLabel}>{i18n.t("summary.waiting")}</span>
                <span className={classes.metricValue}>{advisor.waiting}</span>
              </div>
              <div className={classes.metric}>
                <span className={classes.metricLabel}>{i18n.t("summary.responseAvg")}</span>
                <span className={classes.metricValue}>{advisor.responseAvg}</span>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <h3 className={classes.sectionTitle}>{i18n.t("summary.byLabel")}</h3>
              {advisor.labels.length === 0 && (
                <p className={classes.empty}>{i18n.t("summary.noLabels")}</p>
              )}
              {advisor.labels.map((label) => (
                <div key={label.name} className={classes.labelRow}>
                  <span
                    className={classes.labelTag}
                    style={{ backgroundColor: label.color || "#B9C0FF" }}
                  >
                    {label.name}
                  </span>
                  <span className={classes.bar}>
                    <span
                      className={classes.barFill}
                      style={{
                        width: label.pct,
                        backgroundColor: label.color || "#7E16FF",
                      }}
                    />
                  </span>
                  <span className={classes.labelCount}>{label.count}</span>
                </div>
              ))}
            </div>
          </article>
        ))}
      </div>

      <section className={classes.tableCard} aria-labelledby="waiting-title">
        <Typography component="h2" id="waiting-title" className={classes.tableTitle}>
          {i18n.t("summary.waitingTitle")}
        </Typography>
        {waitingTickets.length === 0 ? (
          <p className={classes.empty}>{i18n.t("summary.noWaiting")}</p>
        ) : (
          <div className={classes.tableScroll}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>{i18n.t("summary.table.client")}</th>
                  <th>{i18n.t("summary.table.advisor")}</th>
                  <th>{i18n.t("summary.table.labels")}</th>
                  <th>{i18n.t("summary.table.waiting")}</th>
                </tr>
              </thead>
              <tbody>
                {waitingTickets.map((ticket) => (
                  <tr key={ticket.id}>
                    <td>
                      <ButtonBase
                        component="button"
                        className={classes.rowButton}
                        onClick={() => history.push(`/tickets/${ticket.id}`)}
                      >
                        {ticket.contact?.name}
                      </ButtonBase>
                    </td>
                    <td>{ticket.whatsapp?.name}</td>
                    <td>
                      <span className={classes.tags}>
                        {(ticket.labels || []).map((label) => (
                          <span
                            key={label.name}
                            className={classes.labelTag}
                            style={{ backgroundColor: label.color || "#B9C0FF" }}
                          >
                            {label.name}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td className={classes.waitingTime}>
                      {formatDuration(
                        (Date.now() - new Date(ticket.lastMessageAt).getTime()) / 1000
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <p className={classes.footnote}>{i18n.t("summary.footnote")}</p>
    </div>
  );
};

export default Summary;
